// Contas da tela de Parceiros na Visão Geral (#6498): parceiros comerciais e
// vendedores parceiros.
//
// - Meus clientes no funil: a situação de hoje de cada negociação da pessoa (e da
//   rede, para o parceiro com vendedores parceiros).
// - Pendências dos clientes: termo esperando assinatura e documento pedido pela
//   análise cadastral, o que o parceiro consegue destravar falando com o cliente.
// - Comissão por cliente aprovado: 1ª parcela (30%) depois da aprovação, 2ª (70%)
//   depois da 1ª fatura (`calcularComissaoParceiro`, mesma conta da Folha).
//
// Lógica pura.

import { calcularComissaoParceiro } from '../comissaoUtils.ts';
import { situacaoNaValidacao } from '../validacaoDados.ts';

export type EtapaCliente = 'contato' | 'simulacao' | 'assinatura' | 'analise' | 'pendencia' | 'aprovado';

export const ETAPAS_CLIENTE: { id: EtapaCliente; rotulo: string }[] = [
  { id: 'contato', rotulo: 'Em contato' },
  { id: 'simulacao', rotulo: 'Simulação feita' },
  { id: 'assinatura', rotulo: 'Termo para assinar' },
  { id: 'analise', rotulo: 'Em validação de dados' },
  { id: 'pendencia', rotulo: 'Com pendência na validação' },
  { id: 'aprovado', rotulo: 'Aprovados (clientes)' },
];

/**
 * Etapa de hoje pelo status da negociação. Perdida e reprovada saem do funil (null).
 * Validação de dados: na fila da analista = 'analise'; já analisado e esperando o cliente
 * = 'pendencia' (regra em src/lib/validacaoDados.ts).
 */
export function etapaDoCliente(status: string | null | undefined): EtapaCliente | null {
  const naValidacao = situacaoNaValidacao(status);
  if (naValidacao === 'fila') return 'analise';
  if (naValidacao === 'pendencia') return 'pendencia';
  switch (status) {
    case 'em_andamento': return 'contato';
    case 'simulacao_realizada': return 'simulacao';
    case 'termo_enviado': return 'assinatura';
    case 'termo_aprovado':
    case 'ganho': return 'aprovado';
    default: return null;
  }
}

export interface ClienteDoParceiro {
  id: number;
  cliente_nome: string | null;
  status: string | null;
  consumo_medio_kwh: number | null;
  responsavel_id: string | null;
  url_termo?: { data_criacao?: string | null }[] | null;
  stage_changed_at?: string | null;
  analise_cadastral?: { decisao_final?: string | null; decisao_final_at?: string | null }[] | null;
}

export type FunilDosClientes = Record<EtapaCliente, { qtd: number; kwh: number }>;

export function funilDosClientes(clientes: readonly ClienteDoParceiro[]): FunilDosClientes {
  const f = Object.fromEntries(ETAPAS_CLIENTE.map(e => [e.id, { qtd: 0, kwh: 0 }])) as FunilDosClientes;
  for (const c of clientes) {
    const e = etapaDoCliente(c.status);
    if (!e) continue;
    f[e].qtd++;
    f[e].kwh += Number(c.consumo_medio_kwh) || 0;
  }
  return f;
}

export interface PendenciaCliente {
  negociacaoId: number;
  cliente: string | null;
  responsavelId: string | null;
  tipo: 'assinatura' | 'documento';
  /** Desde quando: último envio do termo ou a decisão que pediu o documento. */
  desde: string | null;
}

/** Último envio do termo (reenvio conta) ou, sem a data, a última troca de etapa. */
export function envioDoTermo(c: Pick<ClienteDoParceiro, 'url_termo' | 'stage_changed_at'>): string | null {
  const datas = (c.url_termo || []).map(t => t?.data_criacao).filter((d): d is string => !!d).sort();
  return datas[datas.length - 1] ?? c.stage_changed_at ?? null;
}

/** Decisão mais recente da análise cadastral. */
function ultimaDecisao(c: ClienteDoParceiro) {
  return [...(c.analise_cadastral || [])].sort((a, b) => String(b.decisao_final_at ?? '').localeCompare(String(a.decisao_final_at ?? '')))[0] ?? null;
}

/**
 * O que depende do cliente: assinar o termo ou mandar o documento que a análise
 * pediu. A mais antiga primeiro.
 */
export function pendenciasDosClientes(clientes: readonly ClienteDoParceiro[]): PendenciaCliente[] {
  const out: PendenciaCliente[] = [];
  for (const c of clientes) {
    if (c.status === 'termo_enviado') {
      out.push({ negociacaoId: c.id, cliente: c.cliente_nome, responsavelId: c.responsavel_id, tipo: 'assinatura', desde: envioDoTermo(c) });
    } else if (c.status === 'termo_analise') {
      const d = ultimaDecisao(c);
      if (d?.decisao_final === 'Pendência Documental') {
        out.push({ negociacaoId: c.id, cliente: c.cliente_nome, responsavelId: c.responsavel_id, tipo: 'documento', desde: d.decisao_final_at ?? null });
      }
    }
  }
  return out.sort((a, b) => String(a.desde ?? '').localeCompare(String(b.desde ?? '')));
}

export interface ComissaoDoCliente {
  id: number;
  kwh: number;
  /** Grupo A: o gerente calcula à parte; sem valor aqui. */
  grupoA: boolean;
  parcela1: number;
  parcela2: number;
}

/** Comissão de cada cliente aprovado, pela regra da Folha (faixa por kWh × tarifa). */
export function comissaoPorCliente(
  aprovadas: readonly { id: number; kwh: number; grupoA: boolean }[],
  tarifa: number | null,
): ComissaoDoCliente[] {
  return aprovadas.map(a => {
    if (a.grupoA || tarifa == null) return { ...a, parcela1: 0, parcela2: 0 };
    const c = calcularComissaoParceiro(a.kwh, tarifa);
    return { ...a, parcela1: Math.round(c.parcela1 * 100) / 100, parcela2: Math.round(c.parcela2 * 100) / 100 };
  });
}
